// src/services/subscriptionService.js
import { USER_TIERS } from './UserService';
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from '../firebase/firebaseConfig';

// Local storage keys
const STORAGE_KEYS = {
  PREMIUM_STATUS: 'pilatesPulse_premium', // Kept for backward compatibility
  PRO_STATUS: 'pilatesPulse_pro', // New key
  SUBSCRIPTION_DATA: 'pilatesPulse_subscription',
  USER_ID: 'pilatesPulse_userId'
};

/**
 * Check if user has an active pro subscription
 * @returns {boolean} Whether user has pro access
 */
export const isPremiumUser = () => {
  try {
    // Check local storage for premium/pro status
    const hasProStatus = localStorage.getItem(STORAGE_KEYS.PRO_STATUS) === 'true';
    const hasPremiumStatus = localStorage.getItem(STORAGE_KEYS.PREMIUM_STATUS) === 'true';
    
    return hasProStatus || hasPremiumStatus;
  } catch (error) {
    console.error('Error checking pro status:', error);
    return false;
  }
};

/**
 * Get user's subscription data
 * @returns {Object|null} Subscription data if available
 */
export const getSubscriptionData = () => {
  try {
    const subscriptionJson = localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION_DATA);
    return subscriptionJson ? JSON.parse(subscriptionJson) : null;
  } catch (error) {
    console.error('Error getting subscription data:', error);
    return null;
  }
};

/**
 * Get user's subscription from Firestore by user ID
 * @param {string} userId - The user ID to get subscription for
 * @returns {Promise<Object|null>} Subscription data if available
 */
export const getSubscription = async (userId) => {
  try {
    if (!userId) return null;
    
    // First check local storage for cached data
    const cachedData = getSubscriptionData();
    if (cachedData) return cachedData;
    
    // If not in local storage, fetch from Firestore
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists() && userDoc.data().subscription) {
      const subscriptionData = userDoc.data().subscription;
      
      // Cache the subscription data in local storage
      updateSubscription(subscriptionData);
      
      return subscriptionData;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
};

/**
 * Update subscription data and set pro status
 * @param {Object} subscriptionData - The subscription data to save
 */
export const updateSubscription = (subscriptionData) => {
  try {
    // Save subscription data
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION_DATA, JSON.stringify(subscriptionData));
    
    // Set pro status to true
    localStorage.setItem(STORAGE_KEYS.PRO_STATUS, 'true');
    
    // For backward compatibility
    localStorage.setItem(STORAGE_KEYS.PREMIUM_STATUS, 'true');
    
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('proStatusChanged', { detail: { isPro: true } }));
    
    return true;
  } catch (error) {
    console.error('Error updating subscription:', error);
    return false;
  }
};

/**
 * Revoke subscription and pro status
 */
export const revokeSubscription = () => {
  try {
    // Remove pro status
    localStorage.setItem(STORAGE_KEYS.PRO_STATUS, 'false');
    localStorage.setItem(STORAGE_KEYS.PREMIUM_STATUS, 'false');
    
    // Clear subscription data
    localStorage.removeItem(STORAGE_KEYS.SUBSCRIPTION_DATA);
    
    // Dispatch event for components to update
    window.dispatchEvent(new CustomEvent('proStatusChanged', { detail: { isPro: false } }));
    
    return true;
  } catch (error) {
    console.error('Error revoking subscription:', error);
    return false;
  }
};

/**
 * Get days remaining in current subscription
 * @returns {number} Days remaining, or 0 if not pro
 */
export const getDaysRemaining = () => {
  const subscriptionData = getSubscriptionData();
  
  if (!subscriptionData || !subscriptionData.endDate) {
    return 0;
  }
  
  const endDate = new Date(subscriptionData.endDate);
  const currentDate = new Date();
  
  // If already expired
  if (currentDate > endDate) {
    return 0;
  }
  
  // Calculate days remaining
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = Math.ceil((endDate - currentDate) / msPerDay);
  
  return daysRemaining;
};

/**
 * Generate a user ID if none exists
 * @returns {string} User ID
 */
export const getUserId = () => {
  let userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  
  if (!userId) {
    // Generate a simple UUID
    userId = 'user_' + Math.random().toString(36).substring(2, 15) + 
             Math.random().toString(36).substring(2, 15);
    localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
  }
  
  return userId;
};

/**
 * Purchase Pro subscription
 * @param {Object} paymentDetails - Optional payment details
 * @returns {Promise<boolean>} Success status
 */
export const purchasePro = async (paymentDetails = {}) => {
  try {
    // Simulate a payment process
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Create subscription data
    const today = new Date();
    const endDate = new Date();
    endDate.setMonth(today.getMonth() + 1); // 1 month subscription
    
    const subscriptionData = {
      id: `sub_${Date.now()}`,
      status: 'active',
      plan: {
        id: 'pro_monthly',
        name: 'Pro Monthly',
        price: 1.99,
        currency: 'USD',
        interval: 'monthly'
      },
      startDate: today.toISOString(),
      endDate: endDate.toISOString()
    };
    
    // Update local storage
    updateSubscription(subscriptionData);
    
    // If user is authenticated, update Firestore
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const userData = JSON.parse(currentUser);
        if (userData.uid) {
          const userDocRef = doc(db, "users", userData.uid);
          await updateDoc(userDocRef, {
            tier: USER_TIERS.PRO,
            subscription: subscriptionData
          });
        }
      } catch (error) {
        console.error('Error updating user in Firestore:', error);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Purchase failed:', error);
    return false;
  }
};

const subscriptionService = {
  isPremiumUser,
  getSubscriptionData,
  getSubscription,
  updateSubscription,
  revokeSubscription,
  getDaysRemaining,
  getUserId,
  purchasePro
};

export default subscriptionService;